<template>
	<div class="relative md:max-w-[280px] md:w-1/2 w-[48%]">
		<button
			ref="filtersEl"
			class="flex-1 md:flex-none flex items-center justify-center h-[52px] w-full max-w-[213px] border border-black rounded-3xl"
			@click="filterMenuOpened = true"
		>
			<FunnelIcon class="w-5 h-5 text-gray-500 mr-2" />
			<span class="text-sm text-gray-700 font-medium">
				{{ $t("Filters") }}
			</span>
		</button>
		<div>
			<div class="gap-2 flex mb-2 flex-wrap">
				<div
					v-for="filter of selectedFiltersToDisplay"
					:key="filter.Id"
					class="flex items-center h-[42px] shadow-sm px-5 bg-gray hover:bg-gray-dark cursor-pointer"
					@click="toggleFilter(filter.Id, selectedFilters)"
				>
					<span class="text-primary text-base font-medium">
						{{ filter.Name }}
					</span>
					<XMarkIcon class="ml-3 w-5 h-5 cursor-pointer text-gray-super-dark" />
				</div>
			</div>

			<button
				v-if="selectedFiltersToDisplay?.length"
				class="text-gray-900 py-2 underline font-medium text-base"
				@click="selectedFilters = deselectAllFilters()"
			>
				{{ $t("Clear all") }}
			</button>
		</div>

		<TransitionRoot :show="filterMenuOpened" appear as="template">
			<Dialog as="div" @close="close">
				<TransitionChild
					as="template"
					enter="duration-300 ease-out"
					enter-from="opacity-0"
					enter-to="opacity-100"
					leave="duration-200 ease-in"
					leave-from="opacity-100"
					leave-to="opacity-0"
				>
					<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-60" />
				</TransitionChild>
				<TransitionChild
					as="template"
					enter="duration-300 ease-out"
					enter-from="translate-x-full"
					enter-to="translate-x-0"
					leave="duration-200 ease-in"
					leave-from="translate-x-0"
					leave-to="translate-x-full"
				>
					<DialogPanel
						class="flex flex-col fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
					>
						<div class="container py-6 flex items-center justify-between px-4">
							<div>
								<h4 class="font-medium text-lg">{{ $t("Filters") }}</h4>
							</div>
							<button
								type="button"
								class="-m-2.5 rounded-md p-2.5 text-gray-700 outline-none"
								@click="close"
							>
								<span class="sr-only">{{ $t("close_menu") }}</span>
								<XMarkIcon class="h-6 w-6" aria-hidden="true" />
							</button>
						</div>
						<div class="lg:px-5 flex-1 min-h-0 overflow-y-auto">
							<div v-for="(filter, name) in filters" :key="name">
								<CategoryFilters
									v-if="filter"
									v-model="selectedFilters"
									:name="name"
									:filters="filter"
								/>
							</div>
						</div>
						<div class="container p-4">
							<button
								class="w-full text-white text-base font-medium py-3 px-6 bg-gray-800 shadow-sm disabled:opacity-50"
								@click="close"
							>
								{{ $t("Show Products", [getTotal]) }}
							</button>
						</div>
					</DialogPanel>
				</TransitionChild>
			</Dialog>
		</TransitionRoot>
	</div>

	<div class="flex w-full justify-end order-1">
		<button
			type="button"
			class="fixed lg:top-8 top-32 w-12 h-12 z-[11] flex justify-center items-center bg-blue rounded-full transition-opacity"
			:class="{
				'opacity-0 pointer-events-none': !isVisible,
				'opacity-100': isVisible
			}"
			@click="filterMenuOpened = true"
		>
			<span class="sr-only">{{ $t("Filters") }}</span>
			<AdjustmentsHorizontalIcon class="h-8 w-8 text-white" />
		</button>
	</div>
</template>

<script setup lang="ts">
import { ref, toRef } from "vue";
import {
	FunnelIcon,
	XMarkIcon,
	AdjustmentsHorizontalIcon
} from "@heroicons/vue/24/solid";
import {
	Dialog,
	DialogPanel,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";
import type {
	GenericFilterItem,
	GroupedFilters,
	SelectedFilter
} from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";

interface Props {
	filters: GroupedFilters;
	initialSelectedFilters?: SelectedFilter;
}

const { filters, initialSelectedFilters } = defineProps<Props>();

const { deselectFilter, deselectAllFilters, printSelectedFilters } =
	useNorcePwa();

const emit = defineEmits(["sendSelectedFilters"]);
const selectedFilters = ref<SelectedFilter>(initialSelectedFilters || {});
const filtersRef = toRef<() => GroupedFilters>(() => filters);
const selectedFiltersToDisplay = ref<GenericFilterItem[]>(
	printSelectedFilters({
		selectedFilters: selectedFilters.value,
		filters
	})
);
const filterMenuOpened = ref<boolean>(false);
const getTotal = ref(0);
const filtersEl = ref(null);

const { isVisible } = useScrollVisibility(filtersEl, 75);

const close = () => {
	filterMenuOpened.value = false;
};

const emitToCategory = ({
	selectedFilters,
	filters
}: {
	selectedFilters: SelectedFilter | undefined;
	filters: GroupedFilters;
}) => {
	emit("sendSelectedFilters", { selectedFilters }, { resetPageNo: true });

	if (selectedFilters) {
		selectedFiltersToDisplay.value = printSelectedFilters({
			selectedFilters,
			filters
		});
	}
};

const toggleFilter = (
	selectedFilterId: string,
	filters: SelectedFilter | undefined
) => {
	if (filters) {
		selectedFilters.value = deselectFilter(selectedFilterId, filters);
	}
};

watch(selectedFilters, () => {
	emitToCategory({
		selectedFilters: selectedFilters.value,
		filters: filtersRef?.value
	});
});
</script>
